import { Progressbar } from 'framework7-react';
import { FC, Fragment, useEffect } from 'react';

import Markdown from 'markdown-to-jsx';

import API from '@api/API';

import { useCurrentEntertainerMetricsQuery } from '@queries/EntertainerQueries';

import { useDynamicActionMutation } from '@mutations/AppMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useDynamicAction } from '@hooks/useDynamicAction';

import { useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';

import Button from '@components/buttons/Button';
import { AdvancedPieChart, PieChartData } from '@components/charts/AdvancedPieChart';
import { LineChart } from '@components/charts/LineChart/LineChart';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useEntertainerShareLink } from '../ManageEntertainerProfileScreen';
import {
  CardContentType,
  EntertainerMetricsCardHtmlContentProps,
  EntertainerMetricsCardImageContentProps,
  EntertainerMetricsCardLabelRowsContentProps,
  EntertainerMetricsCardLineChartContentProps,
  EntertainerMetricsCardPieChartContentProps,
  EntertainerMetricsCardProps,
} from './EntertainerMetrics.types';

const EntertainerMetricsCardPieChartContent: FC<EntertainerMetricsCardPieChartContentProps> = ({
  className = '',
  chartData,
  legend,
  chartSize = 90,
}) => {
  return (
    <div className={`d-flex gap-3 ${className}`}>
      <div>
        <div style={{ width: chartSize, height: chartSize }}>
          <AdvancedPieChart
            data={chartData}
            // showLabels
            // labelFontSize={12}
            // labelPosition={10}
            // label={({ dataEntry }) => `${dataEntry.value}%`}
          />
        </div>
      </div>
      <div className="d-grid gap-1 w-100">
        {legend.map((row) => (
          <div style={{ fontSize: row.fontSize }}>
            <div className="d-flex justify-content-between align-items-center gap-4">
              <div className="d-flex gap-2 align-items-center">
                {row.marker?.color && (
                  <div style={{ color: row.marker.color, fontSize: 8, marginTop: 2 }}>
                    <FontAwesomeIcon name="circle" />
                  </div>
                )}
                <strong>{row.label}</strong>
              </div>
              {row.value && <div className="ml-auto">{`${row.value}%`}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EntertainerMetricsCardLineChartContent: FC<EntertainerMetricsCardLineChartContentProps> = ({
  className,
  height = 200,
  colors,
  buckets,
}) => {
  return (
    <div className={className}>
      <LineChart height={height} colors={colors} data={buckets} />
    </div>
  );
};

const EntertainerMetricsCardLabelRowsContent: FC<EntertainerMetricsCardLabelRowsContentProps> = ({
  className = 'mt-2',
  rows,
}) => {
  return (
    <div className={`d-grid gap-2 ${className}`}>
      {rows.map((row) => (
        <div key={`${row.label}-${row.progressBar?.value}`}>
          <div className="d-flex justify-content-between align-items-center gap-4">
            <div className="d-flex gap-2">
              {row.marker && (
                <div
                  style={{
                    color: row.marker?.color,
                    fontSize: 12,
                    marginTop: 2,
                  }}
                >
                  <FontAwesomeIcon name="circle" />
                </div>
              )}
              <strong>{row.label}</strong>
            </div>
            {row.progressBar && <div>{`${row.progressBar?.value}%`}</div>}
          </div>
          {row.progressBar?.value && (
            <div className="mt-1">
              <Progressbar
                style={
                  {
                    '--f7-progressbar-progress-color': row.progressBar.color,
                  } as any
                }
                progress={row.progressBar?.value}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const EntertainerMetricsCardImageContent: FC<EntertainerMetricsCardImageContentProps> = ({
  className,
  imgUrl,
}) => {
  return (
    <div className={className}>
      <img src={imgUrl} style={{ width: '100%', height: 'auto' }} />
    </div>
  );
};

const EntertainerMetricsCardHtmlContent: FC<EntertainerMetricsCardHtmlContentProps> = ({
  className,
  html,
}) => {
  return (
    <Markdown className={className} options={{ forceWrapper: true, forceBlock: true }}>
      {html}
    </Markdown>
  );
};

const EntertainerMetricsCard: FC<EntertainerMetricsCardProps> = ({ title, content, footer }) => {
  const { track } = useAnalytics();
  const { isSubmittingDynamicAction, handleDynamicAction } = useDynamicAction();

  return (
    <Card>
      <Card.Body className="p-3">
        <div>
          {title && (
            <div className="fs-6">
              <strong>{title}</strong>
            </div>
          )}
          {content.map((item, index) => (
            <Fragment key={index}>
              {item.type === CardContentType.Pie && (
                <EntertainerMetricsCardPieChartContent {...item.data} />
              )}
              {item.type === CardContentType.Line && (
                <EntertainerMetricsCardLineChartContent {...item.data} />
              )}
              {item.type === CardContentType.LabelRows && (
                <EntertainerMetricsCardLabelRowsContent {...item.data} />
              )}
              {item.type === CardContentType.Image && (
                <EntertainerMetricsCardImageContent {...item.data} />
              )}
              {item.type === CardContentType.Html && (
                <EntertainerMetricsCardHtmlContent {...item.data} />
              )}
            </Fragment>
          ))}
          {footer?.button && (
            <div className="mt-3">
              <Button
                roundness="rounded"
                variant="smooth"
                color="neutral"
                disabled={isSubmittingDynamicAction}
                onClick={async () => {
                  track('Metrics Card Button Clicked', {
                    title,
                    label: footer?.button?.text,
                    action: footer?.button?.action,
                    payload: footer?.button?.payload,
                    to: footer?.button?.to,
                  });

                  await handleDynamicAction(footer?.button);
                }}
              >
                {footer?.button.text}
              </Button>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export const EntertainerMetricsScreen = createScreenComponent<{}>(({ f7route }) => {
  const { entertainerMetricsAreReady, entertainerMetrics } = useCurrentEntertainerMetricsQuery(
    f7route?.query,
  );
  const { didCopy, handleShareLink } = useEntertainerShareLink();

  useEffect(() => {
    // Send to update `read` data
    API.post('/v1/user/entertainer/metrics/view', f7route?.query);
  }, []);

  return (
    <Screen name="Entertainer Metrics Screen" headerProps={{ title: 'Insights' }} hideToolbar>
      <Screen.Content>
        <TitleToolbar
          text={entertainerMetrics?.metricsDetails?.header?.title}
          action={
            <Button
              startIcon={<FontAwesomeIcon name="share" />}
              roundness="rounded"
              variant="smooth"
              color="neutral"
              size="sm"
              className="py-1 px-2"
              onClick={handleShareLink}
            >
              {didCopy ? 'Copied!' : 'Share My Profile'}
            </Button>
          }
          size="md"
        />
        {entertainerMetrics?.metricsDetails?.header?.description &&
          mapStringToParagraphs(entertainerMetrics?.metricsDetails?.header?.description, {
            collapseTopMargin: true,
          })}
        {entertainerMetrics?.metricsDetails?.content?.map((cardProps, index) => (
          <EntertainerMetricsCard key={index} {...cardProps} />
        ))}
      </Screen.Content>
    </Screen>
  );
});
